import React from 'react'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import { graphql } from 'gatsby'
import { generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../utils/schema-generator'
import Parallax from '../components/parallax'
import ServiceTable from '../components/service-table'
import GoogleMapsEmbed from '../components/google-maps-embed'
import { Treatment, Question } from '../interfeces'
import RelatedTreatments from '../components/related-treatments'

interface Props {
  data: any
  location: Location
}

const treatment: Treatment = {
  duration: '30-60 minutes',
  session: '1 session',
  results: 'Results 6-9 months',
  cost: 'From £149.99',
  downtime: 'Minimal',
}

const relatedTreatments = [
  {
    name: 'Dermal Fillers Cheshunt',
    slug: 'dermal-fillers-cheshunt',
    description:
      'Learn how dermal fillers across cheeks and mid-face can complement tear trough rejuvenation.',
  },
  {
    name: 'Cheek Fillers Cheshunt',
    slug: 'cheek-fillers',
    description:
      'Lift and support the mid-face with cheek fillers for a more rested, youthful look.',
  },
  {
    name: 'Botox in Cheshunt',
    slug: 'botox-cheshunt',
    description:
      'Soften expression lines around the eyes with precise Botox anti-wrinkle treatments.',
  },
]

const questions: Array<Question> = [
  {
    question: 'How much do tear trough fillers cost in Cheshunt?',
    answer:
      'Tear trough filler treatments at Ada Aesthetics start from £149.99 per area using premium hyaluronic acid filler. This delicate treatment requires specialist expertise. The final cost depends on the amount of product needed and severity of under-eye hollowing. Book a free consultation to receive a personalized quote.',
  },
  {
    question: 'How do tear trough fillers work?',
    answer:
      'Tear trough fillers at Ada Aesthetics use hyaluronic acid to restore volume beneath the eyes, filling the hollow area between the lower eyelid and upper cheek. This eliminates shadows, reduces the appearance of dark circles, and creates a refreshed, youthful look.',
  },
  {
    question: 'How is the tear trough filler administered?',
    answer:
      'Tear trough fillers are injected directly into the skin under the eyes using very fine needles or cannulas to ensure minimum discomfort. Anaesthetic cream is applied beforehand. After injection, our aesthetic doctor gently massages the area to ensure even distribution.',
  },
  {
    question: 'Are tear trough fillers safe?',
    answer:
      'When administered by qualified professionals like our experienced team at Ada Aesthetics, tear trough fillers are very safe. This is a specialist treatment requiring advanced training due to the delicate under-eye area. We use only FDA-approved hyaluronic acid products.',
  },
  {
    question: 'How long do tear trough fillers last?',
    answer:
      'Tear trough fillers typically last 6-9 months, though some clients see results lasting up to 12 months. The under-eye area has minimal movement, which can help fillers last longer. We recommend follow-up appointments to maintain your refreshed appearance.',
  },
  {
    question: 'Will tear trough fillers make my eyes look puffy?',
    answer:
      'No! When performed correctly by our expert practitioners at Ada Aesthetics, tear trough fillers should reduce puffiness, not create it. We use precise injection techniques and appropriate products designed specifically for the delicate under-eye area.',
  },
  {
    question: 'Can tear trough fillers help with dark circles?',
    answer:
      'Yes! Tear trough fillers are excellent for dark circles caused by hollowing under the eyes. By filling the hollow, we eliminate the shadow that creates the dark appearance. This works best for structural dark circles rather than pigmentation issues.',
  },
  {
    question: 'Is there any downtime after tear trough fillers?',
    answer:
      'Minimal downtime is required after tear trough treatment at Ada Aesthetics. You may experience slight swelling or bruising for 2-3 days, though many clients have none. Most people return to work the same day. We provide detailed aftercare instructions.',
  },
]

const TearTroughFillers: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta

  const serviceSchema = generateServiceSchema(
    'Tear Trough Fillers in Cheshunt',
    'Professional tear trough filler treatments in Cheshunt, Hertfordshire. Reduce under-eye hollowing, dark circles, and tired appearance with premium hyaluronic acid fillers. Expert under-eye rejuvenation.',
    '190'
  )

  const faqSchema = generateFAQSchema(questions.map(q => ({
    question: q.question,
    answer: q.answer
  })))

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://adaaesthetics.uk' },
    { name: 'Treatments', url: 'https://adaaesthetics.uk/treatments' },
    { name: 'Tear Trough Fillers Cheshunt', url: 'https://adaaesthetics.uk/tear-trough-filer' }
  ])

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema, breadcrumbSchema]
  }

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Tear Trough Fillers Cheshunt | Eliminate Dark Circles | Ada Aesthetics"
        description="Expert tear trough filler treatments in Cheshunt. Reduce dark circles, under-eye hollowing & tired appearance. Premium dermal fillers. Book consultation: 07478705252"
        keywords="tear trough fillers Cheshunt, under eye treatment Hertfordshire, dark circles treatment Cheshunt, under eye filler, eye rejuvenation Cheshunt"
        url="https://adaaesthetics.uk/tear-trough-filer"
        schemaData={combinedSchema}
        site={meta}
      />
      <Parallax titleName="Tear Trough Fillers" customClass="page_title" />

      <section className="ls service-single s-pt-70 s-pb-20 s-pb-sm-50 s-py-lg-100 s-pt-xl-150 s-pb-xl-100 c-gutter-60 c-mb-50">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="special-heading">Banish Dark Circles with Tear Trough Fillers</h1>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '-10px', marginBottom: '20px' }}>
                Expert under-eye treatments in Cheshunt, Hertfordshire
              </p>
              <p className="excerpt">
                Exhausted by people asking if you're tired when you feel perfectly rested? Those frustrating under-eye shadows and hollows don't have to define your face anymore. At Ada Aesthetics, our specialist tear trough fillers instantly brighten your eyes, eliminate dark circles, and restore that fresh, youthful glow—no concealer required. Conveniently located in Cheshunt, serving Waltham Cross, Broxbourne, and surrounding Hertfordshire areas.
              </p>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <h2>What Are Tear Trough Fillers?</h2>
              <p>
                Do people ask you if you're feeling tired even though you had a good night's sleep? Maybe concealer
                no longer disguises the dark shadows under your eyes?
              </p>
              <p>
                Over time, the top of our cheekbone drops and creates a hollow under the eye, known as the tear trough.
                This casts a shadow under the eyes which unfortunately gets darker with age, making you look perpetually
                exhausted.
              </p>
              <p>
                At Ada Aesthetics, we use premium dermal fillers containing hyaluronic acid to reduce the
                appearance of under-eye dark circles and hollowing.
              </p>
              <p>
                The result? You'll instantly look more rested, refreshed, and youthful.
              </p>

              <div className="cta-box" style={{
                background: '#f8f9fa',
                padding: '20px',
                borderLeft: '4px solid #d4af37',
                margin: '30px 0'
              }}>
                <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                  Ready to eliminate tired-looking eyes?
                </p>
                <p style={{ marginBottom: '0' }}>
                  Call <a href="tel:07478705252" style={{ color: '#d4af37', fontWeight: 'bold', textDecoration: 'none' }}>07478 705252</a> for your free consultation
                </p>
              </div>

              <h2>Benefits of Tear Trough Fillers</h2>
              <p>Why choose tear trough treatment at Ada Aesthetics?</p>
              <ul>
                <li><strong>Eliminate dark circles</strong> caused by under-eye hollowing</li>
                <li><strong>Reduce tired appearance</strong> - look well-rested and refreshed</li>
                <li><strong>Smooth under-eye hollows</strong> for a youthful contour</li>
                <li><strong>Diminish eye bags</strong> and puffiness</li>
                <li><strong>Brighten the eye area</strong> by eliminating shadows</li>
                <li><strong>Non-surgical solution</strong> - no downtime or surgery</li>
                <li><strong>Immediate results</strong> that improve over 2 weeks</li>
                <li><strong>Long-lasting effects</strong> (6-9 months)</li>
                <li><strong>Natural-looking rejuvenation</strong> - subtle enhancement</li>
              </ul>

              <h2>How Do Tear Trough Fillers Work?</h2>
              <p>
                Dermal fillers serve as a non-permanent replacement which helps restore volume to the under-eye area,
                hydrating and rejuvenating your face. Because hyaluronic acid is found naturally in the skin, the
                filler when injected into the tear trough blends harmoniously with your natural tissue.
              </p>
              <p>
                Tear trough fillers work by:
              </p>
              <ul>
                <li>Filling the hollow depression between lower eyelid and cheek</li>
                <li>Lifting and supporting the delicate under-eye skin</li>
                <li>Eliminating the shadow that creates dark circles</li>
                <li>Smoothing the transition from eye to cheek</li>
                <li>Restoring youthful volume lost through aging</li>
                <li>Hydrating and plumping the skin from within</li>
              </ul>

              <h2>Your Tear Trough Treatment Journey</h2>

              <h3>1. Specialist Consultation</h3>
              <p>
                Tear trough treatment requires specialist expertise due to the delicate under-eye area.
              </p>
              <p>
                Your journey at Ada Aesthetics begins with a thorough consultation where we assess your facial anatomy,
                discuss your concerns, review medical history, and determine if you're a suitable candidate for this
                advanced treatment.
              </p>

              <h3>2. Precise Treatment</h3>
              <p>
                The tear trough filler treatment takes approximately 60 minutes. We apply
                anaesthetic cream for comfort, then use ultra-fine needles or blunt-tip cannulas to precisely inject
                hyaluronic acid into the tear trough area. Our experienced practitioners use advanced injection
                techniques to ensure safe, even distribution.
              </p>

              <h3>3. Immediate Improvement</h3>
              <p>
                You'll see immediate reduction in under-eye hollowing and dark circles. Some swelling is normal,
                particularly for 24-48 hours, but this subsides quickly. Your final results will be visible within
                2 weeks, revealing bright, refreshed, youthful-looking eyes.
              </p>

              <h3>4. Long-Lasting Refreshment</h3>
              <p>
                Tear trough fillers last 6-9 months, sometimes up to 12 months.
              </p>
              <p>
                The under-eye area has minimal movement, which can help results last longer than other facial areas. We recommend
                maintenance appointments to preserve your refreshed appearance.
              </p>

              <h2>Who Is Suitable for Tear Trough Fillers?</h2>
              <p>Tear trough fillers at Ada Aesthetics are ideal if you:</p>
              <ul>
                <li>Have noticeable hollowing under the eyes</li>
                <li>Experience dark circles due to shadowing (not pigmentation)</li>
                <li>Look tired even when well-rested</li>
                <li>Notice deepening of the tear trough with age</li>
                <li>Have lost volume in the mid-face area</li>
                <li>Want to reduce under-eye bags or puffiness</li>
                <li>Prefer non-surgical eye rejuvenation</li>
              </ul>
              <p>
                <strong>Important:</strong> Tear trough treatment is not suitable for everyone. Certain medical
                conditions, very thin skin, or severe under-eye bags may require alternative treatments. We'll
                assess your suitability during your consultation.
              </p>

              <h2>Why Choose Ada Aesthetics for Tear Trough Fillers?</h2>
              <ul>
                <li><strong>Specialist expertise</strong> in advanced under-eye treatments</li>
                <li><strong>Extensive facial anatomy knowledge</strong> - critical for this delicate area</li>
                <li><strong>Premium hyaluronic acid products</strong> - FDA-approved and safe</li>
                <li><strong>Advanced injection techniques</strong> - cannulas and precision methods</li>
                <li><strong>Natural, subtle results</strong> - refreshed, not overdone</li>
                <li><strong>Free consultations</strong> to assess suitability</li>
                <li><strong>Conveniently located</strong> in Cheshunt, Hertfordshire</li>
                <li><strong>Serving local areas</strong> including Waltham Cross, Broxbourne, Hoddesdon</li>
                <li><strong>Competitive pricing</strong> with transparent costs</li>
              </ul>

              <div className="cta-box" style={{
                background: '#fff',
                padding: '25px',
                border: '2px solid #d4af37',
                margin: '40px 0',
                textAlign: 'center'
              }}>
                <h3 style={{ marginBottom: '15px', color: '#d4af37' }}>
                  Free Expert Consultation
                </h3>
                <p style={{ marginBottom: '15px', fontSize: '16px' }}>
                  Discuss your goals with our experienced practitioners
                </p>
                <a
                  href="tel:07478705252"
                  style={{
                    display: 'inline-block',
                    padding: '12px 30px',
                    background: '#d4af37',
                    color: '#fff',
                    textDecoration: 'none',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}
                >
                  Call 07478 705252
                </a>
              </div>

              <h2>Tear Trough Fillers vs Other Under-Eye Treatments</h2>

              <h3>Tear Trough Fillers</h3>
              <ul>
                <li>Fill hollow under eyes, eliminate shadows</li>
                <li>Immediate, visible results</li>
                <li>6-9 months duration</li>
                <li>Non-surgical with minimal downtime</li>
                <li>Best for structural dark circles and hollowing</li>
              </ul>

              <h3>Eye Creams</h3>
              <ul>
                <li>Topical treatment for surface concerns</li>
                <li>Cannot address structural hollowing</li>
                <li>Gradual, subtle effects</li>
                <li>Best for hydration and fine lines</li>
              </ul>

              <h3>Surgery (Blepharoplasty)</h3>
              <ul>
                <li>Removes excess skin or fat</li>
                <li>Invasive with extended recovery</li>
                <li>Permanent results</li>
                <li>Best for severe sagging or excess skin</li>
              </ul>

              <p>
                At Ada Aesthetics, we often combine tear trough fillers with cheek fillers for
                comprehensive mid-face rejuvenation.
              </p>
              <p>
                During your consultation, we'll recommend the best treatment
                plan for your specific concerns.
              </p>

              <p style={{
                background: '#e8f5e9',
                padding: '15px',
                borderRadius: '4px',
                margin: '20px 0'
              }}>
                <strong>💚 Still have questions?</strong> Our friendly team is here to help.
                Call <strong>07478 705252</strong> to speak with a qualified practitioner.
              </p>

              <h2>Your Local Tear Trough Clinic</h2>
              <p>
                Located at 15 Ousden Drive, Cheshunt, EN8 9RL, Ada Aesthetics is your local expert for tear trough
                filler treatments and under-eye rejuvenation.
              </p>
              <p>
                We're easily accessible for residents throughout
                Hertfordshire, including Waltham Cross, Broxbourne, Hoddesdon, Waltham Abbey, and Enfield.
              </p>
              <p>
                No need to travel to London for specialist under-eye treatments - expert tear trough fillers with
                natural, refreshed results are available locally at competitive prices.
              </p>

              <h2>Book Your Tear Trough Consultation</h2>
              <p>
                Ready to eliminate tired-looking eyes and dark circles? Join clients who trust us for this advanced treatment.
                Schedule your free consultation today - our specialist practitioners will assess your suitability and create
                a personalized treatment plan to restore a bright, youthful, refreshed appearance to your under-eye area.
              </p>
              <p>
                Call us today at <strong>07478705252</strong> or book online to schedule your consultation.
                Your complimentary assessment (worth £50) includes under-eye analysis and bespoke treatment plan.
              </p>

              <RelatedTreatments treatments={relatedTreatments} />

              <GoogleMapsEmbed
                embedUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2469.9642841486544!2d-0.04449842346989817!3d51.70251797183743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761f5e6c8f7d0d%3A0x9f3e8f3e8f3e8f3e!2s15%20Ousden%20Drive%2C%20Cheshunt%2C%20Waltham%20Cross%20EN8%209RL!5e0!3m2!1sen!2suk!4v1234567890123"
                height={450}
                title="Ada Aesthetics Tear Trough Fillers Clinic Location in Cheshunt"
                loading="lazy"
              />
            </div>
          </div>

          <ServiceTable treatment={treatment} questions={questions} />
        </div>
      </section>
    </Layout>
  )
}

export default TearTroughFillers

export const pageQuery = graphql`
  query TearTroughFillers {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
